<template>
  <div>
    <page-header
      :actions="pageHeaderActionsConfig"
      :page="pageHeaderConfig"
      right-side-class="col-12 align-items-center d-flex pb-2 justify-content-end"
    />
    <board :details="invoiceDetailsBoard" />

    <div v-if="isLoaded" class="mt-3">
      <validation-observer
        ref="resultSummary"
        novalidate
        onsubmit="return false;"
        tag="form"
      >
        <div class="row mt-5">
          <div class="col-12">
            <section-title has-line title="Núm. de años/meses en tratamiento" />
            <div
              class="row ml-1 mr-1 align-items-center border-bottom pb-3 mb-3"
            >
              <div class="col-1">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-input
                    v-model="treatmentYearsOrMonthsValue"
                    custom-error-msg="El(los) año(s) es(son) requerido(s)."
                    :disabled="true"
                    :error="errors[0]"
                    mask-type="Number"
                    :max="999"
                    :maxlength="'3'"
                    :min="0"
                    :name="treatmentYearsOrMonthsValue"
                    style="max-width: 225px"
                  />
                </validation-provider>
              </div>
              <div class="col d-flex justify-content-start">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-radio
                    v-model="treatmentYearsOrMonths"
                    custom-error-msg="Favor seleccione"
                    :disabled="true"
                    :error="Boolean(errors[0])"
                    :is-inline="true"
                    name="rbTreatment"
                    :options="treatmentOptions"
                  />
                </validation-provider>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <section-title
              has-line
              title="La fecha de la primera evaluación realizada que recomendó el servicio"
            />

            <evaluation-row
              add-title="Presione aquí para añadir evaluación"
              custom-error-msg="Favor seleccionar la evaluación."
              date-title="Fecha de Evaluación:"
              :default-date="catalog.dateOptions.evaluationDate"
              :disabled="model.isSubmitted"
              :first-item="model.evaluation.id == 0"
              :info="model.evaluation"
              @save="saveEvaluationHandler"
            />

            <div v-if="validate">
              <error
                v-if="errors.evaluation.invalid"
                :message="errors.evaluation.message"
                variant="no-border"
              />
            </div>
          </div>
        </div>

        <div class="row mt-5">
          <div class="col-12">
            <section-title
              has-line
              title="Si ha sido reevaluado, precise la información a continuación para cada una de las veces en que fuera evaluado"
            />
            <div
              v-for="(reevaluation, idx) in model.reevaluations"
              :key="`reevaluation-${idx}`"
              class="mb-2"
            >
              <evaluation-row
                add-title="Presione aquí para añadir reevaluación"
                confirm-date-title="Confirmar Fecha de Reevaluación:"
                custom-error-msg="Favor seleccionar la reevaluación."
                :date-options="availablereevaluationDates(reevaluation.date)"
                date-title="Fecha de Reevaluación:"
                :disabled="model.isSubmitted"
                :first-item="reevaluation.id == 0"
                :info="reevaluation"
                re-evaluation
                @cancel="cancelreevaluationHandler(idx)"
                @save="savereevaluationHandler($event, idx)"
              />
            </div>
            <div
              v-if="showAddEvaluationBtn && !model.isSubmitted"
              class="w-100 text-right"
            >
              <action-button
                i="fas fa-file-plus"
                text="Añadir"
                variant="success mt-2"
                @click="addreevaluationHandler"
              />
            </div>
          </div>
        </div>
        <div class="row mt-5">
          <div class="col-12">
            <section-title
              has-line
              title="Desglose las metas a largo plazo desarrolladas en los últimos planes de intervención y el nivel de ejecución alcanzado (logros) en cada uno de los años señalados."
            />
            <long-term-goals
              :academic-years="academicYearsToCreateGoals"
              :disabled="model.isSubmitted"
              :execution-level-archieved="catalog.executionLevelArchieved"
              :goals="model.goals"
              @alert="alertHandler"
            />
            <div v-if="validate">
              <error
                v-if="errors.goals.invalid"
                :message="errors.goals.message"
                variant="no-border"
              />
              <error
                v-if="goalsCountErrorMessage"
                :message="goalsCountErrorMessage"
                variant="no-border"
              />
            </div>
          </div>
        </div>
        <div class="row mt-5">
          <div class="col-12">
            <section-title
              has-line
              title="Resuma los resultados de intervención"
            />
            <div class="ml-3">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-text-area
                  v-model="model.resume"
                  class="mb-2"
                  :count-classes="['d-block text-right']"
                  custom-error-msg="Favor resuma los resultados."
                  :disabled="model.isSubmitted"
                  :error="errors[0]"
                  label="Resuma los resultados:"
                  :maxlength="3000"
                  :show-char-count="true"
                />
              </validation-provider>
              <validation-provider v-slot="{ errors }" rules="required">
                <div>
                  <span class="color-gray-6 font-14 font-weight-normal">
                    Especifique si:
                  </span>

                  <custom-radio
                    v-model="model.resumeType"
                    custom-error-msg="Favor especificar."
                    :disabled="model.isSubmitted"
                    :error="Boolean(errors[0])"
                    :is-inline="false"
                    name="rbInterventionResume"
                    :options="interventionResumeOptions"
                  />
                </div>
              </validation-provider>
            </div>
          </div>
        </div>
        <div class="row mt-5">
          <div class="col-12">
            <section-title
              has-line
              title="Precise las fortalezas del estudiante"
            />
            <div class="bg-secondary-13 p-3 rounded text-center ml-3">
              <div v-if="!isAddStrengthMode">
                <action-button
                  class="border btn px-5"
                  :disabled="model.isSubmitted"
                  i="fas fa-file-plus color-green"
                  text="Presione aquí para añadir fortalezas"
                  @click="isAddStrengthMode = true"
                />
                <custom-chips-group
                  v-if="catalog.addedStrengths.length"
                  class="text-left"
                  :disabled="model.isSubmitted"
                  no-background
                  no-wrap
                  object-text="name"
                  :on-item-deleted="onDeleteStrengthHandler"
                  :selected-items="catalog.addedStrengths"
                  title="Fortaleza(s) seleccionada(s)"
                />
              </div>
              <div v-if="isAddStrengthMode">
                <multi-selection-picker
                  add-item-title-label="Añadir nueva Fortaleza a su listado"
                  :all-items="getAvailableStrengths"
                  allow-item-list-editing
                  class="text-left"
                  :column-count="1"
                  :disabled="model.isSubmitted"
                  edit-item-title-label="Editar Fortaleza de su listado"
                  id-prop-name="id"
                  item-type-name="fortaleza"
                  maxlength="100"
                  modify-removes-item
                  placeholder="Escriba aquí para buscar una"
                  text-prop-name="name"
                  @itemAdded="onAddedStrengthHandler"
                  @itemModified="onModifiedStrengthHandler"
                  @itemSelected="onSelectedStrengthHandler"
                />
                <custom-chips-group
                  v-if="catalog.addedStrengths.length"
                  class="text-left"
                  :disabled="model.isSubmitted"
                  no-background
                  no-wrap
                  object-text="name"
                  :on-item-deleted="onDeleteStrengthHandler"
                  :selected-items="catalog.addedStrengths"
                  title="Fortaleza(s) seleccionada(s)"
                />

                <div
                  class="flex-grow-1 d-flex align-items-center justify-content-end pt-4"
                >
                  <action-button
                    class="px-3 mr-2"
                    :disabled="model.isSubmitted"
                    i="fas fa-times-circle"
                    text="Cancelar"
                    variant="outline-danger"
                    @click="isAddStrengthMode = false"
                  />
                  <action-button
                    class="px-3"
                    :disabled="model.isSubmitted"
                    i="fas fa-arrow-alt-circle-right"
                    text="Continuar"
                    variant="success"
                    @click="onStrengthContinue"
                  />
                </div>
              </div>
            </div>
            <div v-if="validate">
              <error
                v-if="errors.strengths.invalid"
                :message="errors.strengths.message"
                variant="no-border"
              />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 mt-5">
            <section-title
              has-line
              title="Señale los factores que podrían estar afectando el progreso del estudiante en el proceso de intervención"
            />
            <div class="ml-3">
              <div v-for="factor in studentFactorsOptions" :key="factor.id">
                <check-box
                  v-model="factor.check"
                  :disabled="model.isSubmitted"
                  name="studentFactors"
                  :text="factor.label"
                />
              </div>
            </div>

            <div v-if="showStudentFactorsOthers" class="ml-5">
              <div
                v-for="factor in studentFactorsOthersOptions"
                :key="factor.id"
              >
                <check-box
                  v-model="factor.check"
                  :disabled="model.isSubmitted"
                  name="studentOtherFactors"
                  :text="factor.label"
                />
              </div>
              <div v-if="isCustomFactorSelected" class="ml-5">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-text-area
                    v-model="model.otherFactor"
                    :count-classes="['d-block text-right']"
                    custom-error-msg="Favor llenar este campo."
                    :disabled="model.isSubmitted"
                    :error="errors[0]"
                    :maxlength="3000"
                    :show-char-count="true"
                  />
                </validation-provider>
              </div>
            </div>

            <error
              v-if="studentFactorHasError"
              message="Debe seleccionar un factor."
              variant="no-border"
            />
          </div>
        </div>
        <div class="row mt-5">
          <div class="col-12">
            <section-title
              has-line
              title="Si considera que el estudiante requiere continuar en servicio, establezca la necesidad del mismo indicando la meta hacia la que dirigirá las intervenciones, aspectos que está considerando sobre capacidad recuperativa, un estimado de tiempo adicional recomendado y la modalidad."
            />
            <div class="ml-3">
              <validation-provider v-slot="{ errors }" rules="required">
                <div class="border-bottom py-2">
                  <custom-radio
                    v-model="requiresContinueService"
                    custom-error-msg="Favor seleccione"
                    :disabled="model.isSubmitted"
                    :error="Boolean(errors[0])"
                    :is-inline="false"
                    name="rbRequiresContinueService"
                    :options="requiresContinueServiceOptions"
                  />
                </div>
              </validation-provider>
              <div v-if="showContinueService" class="mt-3">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-text-area
                    v-model="continuityServiceComment"
                    :count-classes="['d-block text-right']"
                    custom-error-msg="El campo es requerido"
                    :disabled="model.isSubmitted"
                    :error="errors[0]"
                    :maxlength="3000"
                    :show-char-count="true"
                  />
                </validation-provider>
                <validation-provider v-slot="{ errors }" rules="required">
                  <div class="color-gray-6 font-14 font-weight-normal">
                    Término recomendado:
                  </div>
                  <custom-radio
                    v-model="recomendedTerm"
                    custom-error-msg="Favor seleccione"
                    :disabled="model.isSubmitted"
                    :error="Boolean(errors[0])"
                    is-inline
                    name="rbRecomendedTerm"
                    :options="recomendedTermOptions"
                  />
                </validation-provider>
              </div>
              <div v-if="showContinueService" class="mt-3">
                <validation-provider v-slot="{ errors }" rules="required">
                  <div class="color-gray-6 font-14 font-weight-normal">
                    La capacidad recuperativa del estudiante se estima:
                  </div>
                  <custom-radio
                    v-model="recoveryStudentCapacity"
                    custom-error-msg="Favor seleccione"
                    :disabled="model.isSubmitted"
                    :error="Boolean(errors[0])"
                    is-inline
                    name="rbRecoveryStudentCapacity"
                    :options="recoveryStudentCapacityOptions"
                  />
                </validation-provider>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-5">
          <div class="col-12">
            <section-title
              has-line
              title="Análisis de resultado de intervención"
            />
            <div class="ml-4 pl-2">
              <div class="row mt-5">
                <div class="col-12">
                  <div
                    class="row ml-1 mr-1 align-items-center border-bottom pb-3 mb-3"
                  >
                    <div class="col-3">
                      <validation-provider v-slot="{ errors }" rules="required">
                        <div class="d-flex align-items-center">
                          <span
                            class="color-gray-6 font-16 font-weight-normal mr-2"
                          >
                            Nos recomienda por los pasados:
                          </span>
                          <custom-input
                            v-model="treatmentYearsOrMonthsValue"
                            custom-error-msg="El(los) año(s) es(son) requerido(s)."
                            :disabled="true"
                            :error="errors[0]"
                            style="max-width: 225px"
                          />
                        </div>
                      </validation-provider>
                    </div>
                    <div class="col d-flex justify-content-start">
                      <validation-provider v-slot="{ errors }" rules="required">
                        <custom-radio
                          v-model="treatmentYearsOrMonths"
                          custom-error-msg="Favor seleccione"
                          :disabled="true"
                          :error="Boolean(errors[0])"
                          :is-inline="true"
                          name="rbTreatment2"
                          :options="treatmentOptions"
                        />
                      </validation-provider>
                    </div>
                  </div>
                </div>
              </div>

              <div class="ml-3">
                <div class="border-bottom py-2">
                  <check-box
                    v-model="model.analysis.focusInGoalService"
                    class="my-2"
                    :disabled="model.isSubmitted"
                    text="Continuar en el servicio, enfocando el servicio hacia la meta indicada arriba."
                  />
                </div>
                <div class="border-bottom py-2">
                  <div class="row">
                    <div class="col">
                      <check-box
                        v-model="model.analysis.frequencyTypeCheck"
                        class="my-2"
                        :disabled="model.isSubmitted"
                        text="Continuar en el servicio y disminuir la frecuencia a:"
                      />
                    </div>
                    <div class="col d-flex justify-content-end pr-5">
                      <custom-drop-down
                        v-model="model.analysis.frequencyTypeId"
                        class="w-50"
                        :custom-error-msg="errors.analysisFrequencyType.message"
                        :disabled="model.isSubmitted"
                        :error="
                          validate && errors.analysisFrequencyType.invalid
                        "
                        fieldkey="id"
                        fieldname="name"
                        :options="catalog.frequencyTypes"
                        placeholder="Seleccione"
                        @input="
                          model.analysis.frequencyTypeCheck = !!model.analysis
                            .frequencyTypeId
                        "
                      />
                    </div>
                  </div>
                </div>
                <div class="border-bottom py-2">
                  <div class="row">
                    <div class="col">
                      <check-box
                        v-model="model.analysis.serviceModalityCheck"
                        class="my-2"
                        :disabled="model.isSubmitted"
                        text="Continuar en el servicio en una modalidad diferente:"
                      />
                    </div>
                    <div class="col d-flex justify-content-end pr-5">
                      <custom-drop-down
                        v-model="model.analysis.serviceModalityId"
                        class="w-50"
                        :custom-error-msg="
                          errors.analysisServiceModality.message
                        "
                        :disabled="model.isSubmitted"
                        :error="
                          validate && errors.analysisServiceModality.invalid
                        "
                        fieldkey="id"
                        fieldname="name"
                        :options="catalog.serviceModalities"
                        placeholder="Seleccione"
                        @input="
                          model.analysis.serviceModalityCheck = !!model.analysis
                            .serviceModalityId
                        "
                      />
                    </div>
                  </div>
                </div>
                <div
                  v-if="this.isNewFeaturesImprovementEnabled"
                  class="border-bottom py-2"
                >
                  <div class="row">
                    <div class="col">
                      <check-box
                        v-model="model.analysis.serviceDurationCheck"
                        class="my-2"
                        :disabled="model.isSubmitted"
                        text="Continuar con el servicio en una duración diferente:"
                      />
                    </div>
                    <div class="col d-flex justify-content-end pr-5">
                      <custom-drop-down
                        v-model="model.analysis.serviceDurationId"
                        class="w-50"
                        :custom-error-msg="
                          errors.analysisServiceDurationmessage
                        "
                        :disabled="model.isSubmitted"
                        :error="
                          validate && errors.analysisServiceDuration.invalid
                        "
                        fieldkey="id"
                        fieldname="name"
                        :options="catalog.serviceDurations"
                        placeholder="Seleccione"
                        @input="
                          model.analysis.serviceDurationCheck = !!model.analysis
                            .serviceDurationId
                        "
                      />
                    </div>
                  </div>
                </div>
                <div class="border-bottom py-2">
                  <div class="row">
                    <div class="col">
                      <check-box
                        v-model="model.analysis.periodCheck"
                        class="my-2"
                        :disabled="model.isSubmitted"
                        text="Comenzar y completar el alta en un periodo estimado de:"
                      />
                    </div>
                    <div class="col d-flex justify-content-end pr-5">
                      <custom-input
                        v-model="model.analysis.period"
                        class="pr-3"
                        :custom-error-msg="errors.analysisPeriodAmount.message"
                        :disabled="model.isSubmitted"
                        :error="validate && errors.analysisPeriodAmount.invalid"
                        style="max-width: 225px"
                        @input="
                          model.analysis.periodCheck =
                            !!model.analysis.period ||
                            !!model.analysis.periodTypeId
                        "
                      />
                      <custom-drop-down
                        v-model="model.analysis.periodTypeId"
                        class="w-50"
                        :custom-error-msg="errors.analysisPeriod.message"
                        :disabled="model.isSubmitted"
                        :error="validate && errors.analysisPeriod.invalid"
                        fieldkey="id"
                        fieldname="name"
                        :options="catalog.periods"
                        placeholder="Seleccione"
                        @input="
                          model.analysis.periodCheck =
                            !!model.analysis.period ||
                            !!model.analysis.periodTypeId
                        "
                      />
                    </div>
                  </div>
                </div>
                <div class="border-bottom py-2">
                  <check-box
                    v-model="model.analysis.focusInObjectiveService"
                    class="my-2"
                    :disabled="model.isSubmitted"
                    text="Continuar el servicio con plan que incluya objetivos hacia el alta de la intervención."
                  />
                </div>
                <div class="border-bottom py-2">
                  <check-box
                    v-model="model.analysis.reevaluationForUpdates"
                    class="my-2"
                    :disabled="model.isSubmitted"
                    text="Reevaluar para actualizar datos que abonen al curso del tratamiento.."
                  />
                </div>
              </div>
              <div v-if="validate">
                <error
                  v-if="errors.analysis.invalid"
                  :message="errors.analysis.message"
                  variant="no-border"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-5 ml-2 mb-5">
          <section-title has-line title="Firma de Especialista" />
          <div class="col-12 ml-2">
            <SpecialistInformationWriter
              v-model="specialistModel"
              :disabled="model.isSubmitted"
              :is-new-features-improvement-enabled="
                isNewFeaturesImprovementEnabled
              "
              :service-specialists="serviceSpecialists"
              :writer-specialists="writerSpecialists"
              @getSpecialistDetail="getSpecialistDetail"
            />
          </div>
        </div>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import Board from 'Board';
import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import EvaluationRow from './components/EvaluationRow';
import CustomInput from 'CustomInput';
import CustomTextArea from 'CustomTextArea';
import CheckBox from 'CheckBox';
import CustomRadio from 'custom-radio';
import CustomChipsGroup from 'CustomChipsGroup';
import MultiSelectionPicker from '@/components/common/MultiSelectionPicker';
import CustomDropDown from 'custom-drop-down';
import alertMixin from 'utils/mixins/alert.mixin';
import Error from '@/components/common/Error';
import { mapState } from 'vuex';
import LongTermGoals from '@/views/provider/referral-management/search/components/LongTermGoals.vue';

import { name as userModuleName } from '@/modules/user/store';
import { mapGetters } from 'vuex';

import {
  getStudentReferralAdmision,
  getSpecialistsForAssignment,
} from '@/modules/shared/studentreferral.api';
import {
  getCodeExecutionLevelArchieved,
  getEvaluationDates,
  saveServiceResultSummary,
  getServiceResultSummary,
  getSpecialistInfo as _getSpecialistInfo,
} from '@/modules/referralManagement/api';
import { getAcademicYearsToType } from '@/modules/shared/academicyear.api';
//recomendation intervention services
import {
  getFrequencyType as _getFrequencyType,
  getPeriods as _getPeriods,
} from '@/modules/shared/code.api.js';
import {
  getServiceModalities as _getServiceModalities,
  getTherapyDurations as _getServiceDurations,
  specialistsList as getSpecialistList,
} from '@/modules/provider/api.js';
import moment from 'moment';
import _generalApi from '@/modules/app/api';
import _ from 'lodash';
import SpecialistInformationWriter from './components/SpecialistInformationWriter.vue';
import { getSysparameterByNames } from '@/modules/shared/sysparameter.api';
import sysparameterByNames from '@/utils/constants/sysParameterNames';

const treatment = {
  Years: 1,
  Months: 2,
};

const EXECUTION_LEVEL_NA = 6;

const initialModel = () => ({
  otherFactor: '',
  signed: null,
  evaluation: {
    id: 0,
    name: '',
    isFirstEvaluation: true,
  },
  analysis: {
    years: null,
    months: null,
    focusInGoalService: false,
    frequencyTypeId: null,
    frequencyTypeCheck: null,
    serviceModalityId: null,
    serviceModalityCheck: null,
    serviceDurationId: null,
    serviceDurationCheck: null,
    periodTypeId: null,
    period: null,
    periodCheck: null,
    focusInObjectiveService: false,
    reevaluationForUpdates: false,
  },
  goals: [],
  reevaluations: [{ id: 0 }],
  therapyDuration: null,
  specialistSignature: [],
  isSubmitted: false,
  strengths: [],
  resumeType: { id: null },
  resume: null,
});

export default {
  name: 'ResultsSummary',
  components: {
    Board,
    PageHeader,
    SectionTitle,
    CustomDropDown,
    ActionButton,
    MultiSelectionPicker,
    EvaluationRow,
    CustomChipsGroup,
    CustomInput,
    CustomTextArea,
    CheckBox,
    LongTermGoals,
    CustomRadio,
    Error,
    SpecialistInformationWriter,
  },
  mixins: [alertMixin],
  data: () => ({
    admissionInfo: {},
    idCounterStrength: 0,
    validate: false,
    model: initialModel(),
    yearsOrMonths: { id: null },
    yearsOrMonthsValue: null,
    treatmentYearsOrMonths: { id: null },
    treatmentYearsOrMonthsValue: null,
    studentFactors: { id: null },
    studentFactorsOther: { id: null },
    requiresContinueService: { id: null },
    recomendedTerm: { id: null },
    recoveryStudentCapacity: { id: null },
    flags: {
      addEvaluationMode: false,
    },
    catalog: {
      dateOptions: {
        evaluationDate: null,
        reevaluationDate: [],
        reevaluationDateOriginal: [],
      },
      academicYears: [],
      strengths: [],
      addedStrengths: [],
      periods: [],
      frequencyTypes: [],
      serviceModalities: [],
      serviceDurations: [],
      executionLevelArchieved: [],
    },
    isAddStrengthMode: null,
    studentFactorHasError: false,
    studentFactorsOptions: [],
    studentFactorsOthersOptions: [],
    continuityServiceComment: null,
    serviceSpecialists: [],
    writerSpecialists: [],
    specialistModel: {
      selectedServiceSpecialist: {},
      serviceSpecialist: null,
      writerSpecialist: null,
    },
    goalsCountErrorMessage: null,
    parameterValues: [],
    isPriorAcademicYear: false,
    isLoaded: false,
  }),
  computed: {
    ...mapState('$_user', { user: 'user', providers: 'companies' }),
    ...mapGetters(userModuleName, ['fullProfile']),
    admissionId() {
      return this.$route.params.admissionId;
    },
    interventionServiceResultSummaryId() {
      return this.$route.params.interventionServiceResultSummaryId; // only has value when a result summary is queried from the history
    },
    isCustomFactorSelected() {
      return (
        this.showStudentFactorsOthers &&
        this.studentFactorsOthersOptions.some(
          (x) => x.name === 'custom-factor-field' && x.check
        )
      );
    },
    factorQuestionIdNamedOther() {
      return 740; // JSF: question id for showing the factor's section below "[x] Otros"
    },
    availableEvaluationDates() {
      return this.catalog.dateOptions.reevaluationDate.filter((date) =>
        this.model.reevaluations.every((r) => {
          const result = this.isObject(r.date) ? r.date.name : r.date;
          return result != date.name;
        })
      );
    },
    showStudentFactorsOthers() {
      return this.studentFactorsOptions.some(
        (x) => x.id === this.factorQuestionIdNamedOther && x.check
      );
    },
    showAddEvaluationBtn() {
      return (
        this.availableEvaluationDates.length &&
        this.model.reevaluations[0].diagnosis &&
        !this.flags.addEvaluationMode
      );
    },
    showContinueService() {
      return this.requiresContinueService
        ? this.requiresContinueService.id == 394
        : false;
    },
    getAvailableStrengths() {
      const selected = this.catalog.addedStrengths.map((x) => {
        return x.name;
      });
      return this.catalog.strengths.filter((x) => !selected.includes(x.name));
    },
    recommendedOptions() {
      return [
        {
          id: 1,
          name: 'Años',
          value: 1,
          label: 'Años',
          check: false,
          invalid: false,
        },
        {
          id: 2,
          name: 'Meses',
          value: 2,
          label: 'Meses ',
          check: false,
          invalid: false,
        },
      ];
    },
    treatmentOptions() {
      return [
        {
          id: 1,
          name: 'Años',
          value: 1,
          label: 'Años',
          check: false,
          invalid: false,
        },
        {
          id: 2,
          name: 'Meses',
          value: 2,
          label: 'Meses ',
          check: false,
          invalid: false,
        },
      ];
    },
    recomendedTermOptions() {
      return [
        {
          id: 395,
          name: '395',
          value: 395,
          label: '1 año adicional',
          check: false,
          invalid: false,
        },
        {
          id: 396,
          name: '396',
          value: 396,
          label: '1 a 2 años adicionales',
          check: false,
          invalid: false,
        },
        {
          id: 397,
          name: '397',
          value: 397,
          label: '2 a 3 años adicionales',
          check: false,
          invalid: false,
        },
      ];
    },
    recoveryStudentCapacityOptions() {
      return [
        {
          id: 398,
          name: '398',
          value: 398,
          label: 'Favorable',
          check: false,
          invalid: false,
        },
        {
          id: 399,
          name: '399',
          value: 399,
          label: 'Desfavorable',
          check: false,
          invalid: false,
        },
        {
          id: 400,
          name: '400',
          value: 400,
          label: 'Condicionada',
          check: false,
          invalid: false,
        },
        {
          id: 401,
          name: '401',
          value: 401,
          label: 'Reservada',
          check: false,
          invalid: false,
        },
      ];
    },
    requiresContinueServiceOptions() {
      return [
        {
          id: 393,
          name: '393',
          value: 393,
          label:
            'No aplica, no se recomienda que el estudiante continúe con el servicio.',
          check: false,
          invalid: false,
        },
        {
          id: 394,
          name: '394',
          value: 394,
          label:
            'Se recomienda que el estudiante continúe en servicio enfocado',
          check: false,
          invalid: false,
        },
      ];
    },
    interventionResumeOptions() {
      return [
        {
          id: 1,
          name: '1',
          value: 1,
          label: 'Es admisión o traslado reciente.',
          check: false,
          invalid: false,
        },
        {
          id: 2,
          name: '2',
          value: 2,
          label: 'Hubo cambio reciente de especialista.',
          check: false,
          invalid: false,
        },
        {
          id: 3,
          name: '3',
          value: 3,
          label:
            'Se ha manifestado pérdida de comunicación con padre/madre/encargado.',
          check: false,
          invalid: false,
        },
        {
          id: 4,
          name: '4',
          value: 4,
          label: 'Ninguna de las anteriores.',
          check: false,
          invalid: false,
        },
      ];
    },
    invoiceDetailsBoard() {
      return [
        [
          {
            label: 'Estudiante:',
            icon: 'fas fa-address-book',
            data: `${this.admissionInfo.studentSieId} - ${this.admissionInfo.fullName}`,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Edad:',
            icon: 'fas fa-calendar-edit',
            data: `${this.admissionInfo.studentAge} años`,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Grado:',
            icon: 'fas fa-calendar-edit',
            data: this.admissionInfo.grade,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Impedimento:',
            icon: 'fas fa-ballot-check',
            data: this.admissionInfo.impediment,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Región:',
            icon: 'fas fa-map-marker-alt',
            data: this.admissionInfo.region,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Municipio:',
            icon: 'fas fa-map-marker-alt',
            data: this.admissionInfo.municipality,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Escuela:',
            icon: 'fas fa-graduation-cap',
            data: this.admissionInfo.place,
            cols: 'col-12 col-md-3',
          },
        ],
        [
          {
            label: 'Tipo de Servicio:',
            data: this.admissionInfo.disciplineName,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Modalidad:',
            data: this.admissionInfo.modalityTypeName,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Frecuencia:',
            data: this.admissionInfo.frequency,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Duración:',
            data: this.admissionInfo.duration,
            cols: 'col-4 col-sm col-12 mt-3',
          },
        ],
      ];
    },
    pageHeaderConfig() {
      return {
        icon: 'fas fa-user-tie',
        title: 'Resumen de Resultados de Servicios de Intervención',
        routes: ['Proveedor', 'Manejo de Referido', 'Resumen de Resultados'],
      };
    },
    pageHeaderActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success text-nowrap',
          click: () =>
            this.$router.push({
              name: 'referral-search',
              params: {
                reload: true,
              },
            }),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'outline-success text-nowrap',
          visible: !this.model.isSubmitted,
          click: () => this.onSave(),
        },
        {
          text: 'Historial Resumen de Resultados',
          icon: 'fas fa-history',
          variant: 'outline-success text-nowrap',
          visible: this.model.hasHistory,
          click: () =>
            this.$router.push({
              name: 'resultsSummaryHistory',
              params: {
                admissionId: this.admissionId,
                isNewFeaturesImprovementEnabled: this
                  .isNewFeaturesImprovementEnabled,
              },
            }),
        },
        {
          text: 'Nuevo Resumen de Resultados',
          icon: 'fas fa-files-medical',
          variant: 'success text-nowrap',
          visible:
            this.model.isSubmitted && !this.interventionServiceResultSummaryId,
          click: this.newResultsSummary,
        },
        {
          text: 'Someter',
          icon: 'fas fa-flag-alt',
          variant: 'success text-nowrap',
          visible: !this.model.isSubmitted,
          click: () => this.onSubmit(),
        },
        {
          text: 'Imprimir',
          icon: 'fas fa-print',
          variant: 'outline-success text-nowrap',
          visible: this.model.isSubmitted,
          click: this.printResultSummary,
        },
      ];
    },
    errors() {
      return {
        evaluation: {
          invalid: !this.model.evaluation.diagnosis,
          message: 'Favor documentar la fecha de la primera evaluación.',
        },
        goals: {
          invalid:
            this.model.goals == 0 ||
            this.model.goals.filter((goal) => !this.isGoalValid(goal)).length >
              0,
          message: 'Favor desglozar las metas a largo plazo.',
        },
        strengths: {
          invalid: this.model.strengths == 0,
          message: 'Favor precisar las fortalezas.',
        },
        analysis: {
          invalid:
            !this.model.analysis.focusInGoalService &&
            !this.model.analysis.frequencyTypeId &&
            !this.model.analysis.serviceModalityId &&
            !this.model.analysis.serviceDurationId &&
            !this.model.analysis.periodTypeId &&
            !this.model.analysis.focusInObjectiveService &&
            !this.model.analysis.reevaluationForUpdates,
          message: 'Favor seleccione al menos una opción.',
        },
        analysisFrequencyType: {
          invalid:
            this.model.analysis.frequencyTypeCheck &&
            !this.model.analysis.frequencyTypeId,
          message: 'La frecuencia es requerida.',
        },
        analysisServiceModality: {
          invalid:
            this.model.analysis.serviceModalityCheck &&
            !this.model.analysis.serviceModalityId,
          message: 'La modaliad es requerida.',
        },
        analysisServiceDuration: {
          invalid:
            this.model.analysis.serviceDurationCheck &&
            !this.model.analysis.serviceDurationId,
          message: 'La duración es requerida.',
        },
        analysisPeriodAmount: {
          invalid:
            this.model.analysis.periodCheck && !this.model.analysis.period,
          message: 'La cantidad es requerida.',
        },
        analysisPeriod: {
          invalid:
            this.model.analysis.periodCheck &&
            !this.model.analysis.periodTypeId,
          message: 'El periodo es requerido.',
        },
      };
    },
    admissionDisciplineTypeId() {
      return this.admissionInfo?.disciplineTypeId;
    },
    isNewFeaturesImprovementEnabled() {
      const paramValue = this.parameterValues.find(
        (x) =>
          x.name == sysparameterByNames.ResultSummaryFeatureImprovementEnabled
      );

      return !paramValue
        ? false
        : paramValue.staticValue.toUpperCase() == 'TRUE';
    },
    academicYearsToCreateGoals() {
      return this.catalog.academicYears.filter(
        (c) => c.id <= this.admissionInfo.academicYearId
      );
    },
  },
  async created() {
    await this.getServiceModalities();
    await this.getServiceDurations();
    await this.getPeriods();
    await this.getFrequencyType();
    await this.fetchParameters();

    await Promise.all([
      this.getAdmissionInfo(),
      this.getCodeExecutionLevelArchieved(),
      this.getAcademicYearsToType(),
    ]).then(async () => await this.getEvaluationDates());

    await this.getNewResultSummaryFromPriorAcademicYear();
    this.isLoaded = true;
  },
  methods: {
    availablereevaluationDates(currentDate) {
      currentDate = moment(currentDate?.default ?? currentDate).isValid()
        ? this.formatDate(currentDate)
        : currentDate;

      return this.catalog.dateOptions.reevaluationDate.filter((date) =>
        this.model.reevaluations.every((r) => {
          const result = this.isObject(r.date) ? r.date.name : r.date;
          return (
            result != this.formatDate(date.default) ||
            this.formatDate(date.default) == currentDate
          );
        })
      );
    },
    isObject(o) {
      return o instanceof Object && o.constructor === Object;
    },
    async getAdmissionInfo() {
      const { data } = await getStudentReferralAdmision(this.admissionId);
      this.admissionInfo = { ...data };
      await this.getSummary();
    },
    async getCodeExecutionLevelArchieved() {
      const { data } = await getCodeExecutionLevelArchieved();
      this.catalog.executionLevelArchieved = data;
    },
    async getEvaluationDates() {
      const { data } = await getEvaluationDates(
        this.admissionInfo.studentSieId,
        this.admissionInfo.disciplineTypeId
      );
      this.catalog.dateOptions = {
        evaluationDate: data.initialEvaluationDate,
        reevaluationDate: data.additionalEvaluationDates.map((item, idx) => ({
          id: idx,
          name: this.formatDate(item.id) + ' - ' + item.name,
          default: item.id,
        })),
      };
    },
    async getAcademicYearsToType() {
      const { data } = await getAcademicYearsToType('CURRENT', 21);
      this.catalog.academicYears = data;
    },
    saveEvaluationHandler(evaluation) {
      this.model.evaluation = evaluation;
    },
    savereevaluationHandler(reevaluation, idx) {
      const newreevaluation = { ...reevaluation };
      newreevaluation.date = newreevaluation.date.name || newreevaluation.date;
      this.model.reevaluations.splice(idx, 1, newreevaluation);
      this.flags.addEvaluationMode = false;
    },
    addreevaluationHandler() {
      this.model.reevaluations.push({});
      this.flags.addEvaluationMode = true;
    },
    cancelreevaluationHandler(idx) {
      this.model.reevaluations.length == 1
        ? (this.model.reevaluations = [{}])
        : this.model.reevaluations.splice(idx, 1);
      this.flags.addEvaluationMode = false;
    },
    onSelectedStrengthHandler(item) {
      this.catalog.addedStrengths.push({
        id: item.id,
        name: item.name,
        highlight: true,
      });
    },
    onAddedStrengthHandler(itemText) {
      this.catalog.strengths.push({
        id: (this.idCounterStrength += -1),
        name: itemText,
      });
    },
    onModifiedStrengthHandler(item, index) {
      if (item.id > 0) {
        item.isDirty = true;
      }
      this.catalog.strengths.splice(index, 0, item);
    },
    onDeleteStrengthHandler(item) {
      this.catalog.addedStrengths.splice(
        this.catalog.addedStrengths.indexOf(item),
        1
      );
    },
    alertHandler(errorMsg = null) {
      if (errorMsg) {
        this.ShowToast('Atención', errorMsg, 'error');
        return;
      }
      this.ShowToast(
        'Proceso Exitoso',
        'La información ha sido guardada exitosamente.',
        'success'
      );
    },
    async getFrequencyType() {
      const { data } = await _getFrequencyType();
      this.catalog.frequencyTypes = data;
    },
    async getServiceModalities() {
      const { data } = await _getServiceModalities({ forTh: true });
      this.catalog.serviceModalities = data;
    },
    async getServiceDurations() {
      const { data } = await _getServiceDurations({ forPei: true });
      this.catalog.serviceDurations = data;
    },
    async getPeriods() {
      const { data } = await _getPeriods();
      this.catalog.periods = data;
    },
    async getNewResultSummaryFromPriorAcademicYear() {
      if (this.isPriorAcademicYear === true) {
        await this.onSave(true);
        this.$router.go();
      }
    },
    async onSave(isNew = false) {
      let data = {
        ...initialModel(),
        reevaluations: [],
        answers: [],
        resumeType: null,
        specialistSignature: null,
        studentSieId: this.admissionInfo.studentSieId,
        studentServiceId: this.admissionInfo.studentServiceId,
        academicYearId: this.admissionInfo.academicYearId,
        evaluation: this.model.evaluation,
      };

      data.reevaluations = this.model.reevaluations
        .filter((x) => x.diagnosis)
        .map((item) => {
          return {
            diagnosis: item.diagnosis,
            date: item.confirmedDate,
            confirmedDate: item.confirmedDate,
          };
        });

      if (!isNew) {
        data = {
          ...data,
          id: this.model.id,
          strengths: this.model.strengths,
          // JSF: open field for writing custom factor.
          otherFactor: this.isCustomFactorSelected
            ? this.model.otherFactor
            : null,
        };

        if (this.model.specialistSignature.length > 0) {
          data.specialistSignature = this.model.specialistSignature[0];
          data.specialistSignature.userId = this.fullProfile.sub;
        }

        this.requiresContinueServiceOptions
          .filter((x) => x.check)
          .forEach((item) => {
            data.answers.push({
              questionId: 750,
              questionTypeId: 52,
              answerId: item.id,
            });
          });

        this.recomendedTermOptions
          .filter((x) => x.check)
          .forEach((item) => {
            data.answers.push({
              questionId: 751,
              questionTypeId: 52,
              answerId: item.id,
            });
          });

        this.recoveryStudentCapacityOptions
          .filter((x) => x.check)
          .forEach((item) => {
            data.answers.push({
              questionId: 752,
              questionTypeId: 52,
              answerId: item.id,
            });
          });

        this.studentFactorsOptions
          .filter((x) => x.check)
          .forEach((item) => {
            data.answers.push({
              questionId: item.id,
              questionTypeId: 52,
              answerId: 138,
            });
          });

        this.studentFactorsOthersOptions
          .filter((x) => x.check)
          .forEach((item) => {
            data.answers.push({
              questionId: item.id,
              questionTypeId: 52,
              answerId: 138,
            });
          });

        data.resumeType = this.model.resumeType.id;
        data.resume = this.model.resume;
        data.continuityServiceComment = this.continuityServiceComment;
        data.analysis = {};

        data.analysis.years =
          this.treatmentYearsOrMonths && this.treatmentYearsOrMonths.id == 1
            ? this.treatmentYearsOrMonthsValue
            : null;
        data.analysis.months =
          this.treatmentYearsOrMonths && this.treatmentYearsOrMonths.id == 2
            ? this.treatmentYearsOrMonthsValue
            : null;

        data.analysis.frequencyTypeId = this.model.analysis.frequencyTypeId?.id;
        data.analysis.serviceModalityId = this.model.analysis.serviceModalityId?.id;
        data.analysis.serviceDurationId = this.model.analysis.serviceDurationId?.id;
        data.analysis.periodTypeId = this.model.analysis.periodTypeId?.id;
        data.analysis.focusInGoalService = this.model.analysis.focusInGoalService;
        data.analysis.focusInObjectiveService = this.model.analysis.focusInObjectiveService;
        data.analysis.reevaluationForUpdates = this.model.analysis.reevaluationForUpdates;
        data.analysis.period = +this.model.analysis.period;

        data.goals = [];

        if (
          this.model.goals.filter((goal) => !this.isGoalValid(goal)).length > 0
        ) {
          this.ShowToast(
            'Alerta',
            'Favor desglozar las metas a largo plazo.',
            'error'
          );
          return;
        } else {
          this.model.goals.forEach((item) => {
            data.goals.push({
              academicYearId: item.academicYear.id,
              executionLevelArchievedId: item.executionLevelArchieved.id,
              percent: !_.isNil(item.executionLevelPercent)
                ? parseInt(item.executionLevelPercent)
                : 0,
              goal: item.selectedGoal.name,
            });
          });
        }
        data.serviceSpecialistId = this.specialistModel?.serviceSpecialist?.id;
        data.writerSpecialistId = this.specialistModel?.writerSpecialist?.id;

        data.isSubmitted = this.model.isSubmitted;
      } else {
        //BC: pass goals to the new result summary
        if (
          this.model.goals.filter((goal) => !this.isGoalValid(goal)).length > 0
        ) {
          this.ShowToast(
            'Alerta',
            'Favor desglozar las metas a largo plazo.',
            'error'
          );
          return;
        } else {
          data.goals = this.model.goals.map((item) => ({
            id: null,
            academicYearId: item.academicYear.id,
            executionLevelArchievedId: item.executionLevelArchieved.id,
            goal: item.selectedGoal.name,
            percent: item.executionLevelPercent,
          }));
        }
      }

      return new Promise((resolve, reject) => {
        saveServiceResultSummary(data)
          .then(() => {
            this.ShowToast(
              'Proceso Exitoso',
              'La información ha sido guardada exitosamente.',
              'success'
            );
            resolve(data);
          })
          .catch((error) => {
            this.ShowToast('', error.response.data.message, 'error');
            reject(error);
          });
      });
    },
    async onSubmit() {
      this.validate = true;
      this.studentFactorHasError = false;

      // validate form
      const isValid = await this.$refs.resultSummary.validate();

      const isEvaluationValid = !!this.model.evaluation.diagnosis;

      this.studentFactorHasError = this.showStudentFactorsOthers
        ? !this.studentFactorsOthersOptions.some((x) => x.check)
        : !this.studentFactorsOptions.some((x) => x.check);

      const years =
        this.treatmentYearsOrMonths &&
        this.treatmentYearsOrMonths.id == treatment.Years
          ? this.treatmentYearsOrMonthsValue
          : Math.ceil(this.treatmentYearsOrMonthsValue / 12);

      this.goalsCountErrorMessage = null;

      if (
        !this.model.goals.some((x) => {
          return x.academicYear.id === this.admissionInfo.academicYearId;
        })
      ) {
        this.goalsCountErrorMessage = `Es requerido al menos una meta para el año ${this.admissionInfo.academicYearDescription}.`;
        this.ShowToast('Alerta', this.goalsCountErrorMessage, 'error');
        return;
      }

      if (years >= 5 && this.model.goals.length < 5) {
        this.goalsCountErrorMessage =
          'Es requerido documentar al menos 5 metas según el tiempo en tratamiento.';
        this.ShowToast('Alerta', this.goalsCountErrorMessage, 'error');
        return;
      }

      if (years < 5 && this.model.goals.length < years) {
        this.goalsCountErrorMessage = `Es requerido documentar al menos ${years} metas según el tiempo en tratamiento.`;
        this.ShowToast('Alerta', this.goalsCountErrorMessage, 'error');
        return;
      }

      if (
        !isValid ||
        !isEvaluationValid ||
        this.studentFactorHasError ||
        this.errors.evaluation.invalid ||
        this.errors.goals.invalid ||
        this.errors.analysis.invalid ||
        this.errors.strengths.invalid ||
        this.errors.analysisFrequencyType.invalid ||
        this.errors.analysisServiceModality.invalid ||
        this.errors.analysisServiceDuration.invalid ||
        this.errors.analysisPeriodAmount.invalid ||
        this.errors.analysisPeriod.invalid
      ) {
        this.ShowToast('Alerta', 'Información Incompleta', 'error');
        return;
      }

      this.model.isSubmitted = true;
      await this.onSave();
      this.$router.go();
    },
    async newResultsSummary() {
      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea crear Nuevo Resumen de Resultados de Servicios de Intervención?`,
        'Si',
        'No'
      );
      if (!answer) return;
      await this.onSave(true);
      this.$router.go();
    },
    setOtherFactorCheckbox(newValue) {
      // JSF: checbox for other section -> check it as true.
      const showOtherSection = this.studentFactorsOptions.find(
        (x) => x.id === this.factorQuestionIdNamedOther && !x.check
      );
      // JSF: Mark the checkboxes for Section Factors.
      if (showOtherSection) showOtherSection.check = newValue;
    },
    setFactorsCheckBox(answers, otherFactorText) {
      answers.forEach((answer) => {
        const factor = this.studentFactorsOptions.find(
          (x) => x.id === answer.questionId
        );
        if (factor) {
          factor.check = true;
        }

        const otherFactor = this.studentFactorsOthersOptions.find(
          (x) => x.id === answer.questionId
        );
        if (otherFactor) {
          otherFactor.check = true;
          this.setOtherFactorCheckbox(true);
        }
      });
      otherFactorText = otherFactorText ?? '';
      if (otherFactorText.length > 0) {
        this.setOtherFactorCheckbox(true);

        const otherFactorOpenField = this.studentFactorsOthersOptions.find(
          (x) => x.name === 'custom-factor-field' && !x.check
        );
        if (otherFactorOpenField) otherFactorOpenField.check = true;
        this.model.otherFactor = otherFactorText;
      }
    },
    async getSummary() {
      const { data } = await getServiceResultSummary({
        studentSieId: this.admissionInfo.studentSieId,
        studentServiceId: this.admissionInfo.studentServiceId,
        academicYearId: this.admissionInfo.academicYearId,
        interventionServiceResultSummaryId: this
          .interventionServiceResultSummaryId,
        disciplineTypeId: this.admissionDisciplineTypeId,
      });

      this.specialistModel.serviceSpecialist = data.serviceSpecialist;
      this.specialistModel.writerSpecialist = data.writerSpecialist;

      if (this.specialistModel.serviceSpecialist == null) {
        this.specialistModel.serviceSpecialist = this.admissionInfo.serviceSpecialist;
      }

      await this.getSpecialists();
      this.getSpecialistDetail();

      this.treatmentYearsOrMonthsValue = data.years || data.months;
      const treatmentSelected = data.years ? 1 : data.months ? 2 : null;

      this.treatmentOptions.forEach((item) => {
        item.check = treatmentSelected == item.id;
      });

      this.treatmentYearsOrMonths = this.treatmentOptions.find((x) => x.check);

      this.studentFactorsOptions = this.mapFactors(data.factors);
      this.studentFactorsOthersOptions = this.mapFactors(data.otherFactors);
      // JSF: Added the custom field with an open textarea
      this.studentFactorsOthersOptions.push({
        name: 'custom-factor-field',
        label: 'Otro.',
        check: false,
        invalid: false,
      });

      if (data.evaluation) {
        this.model.evaluation = {
          diagnosis: data.evaluation.diagnosis,
          isFirstEvaluation: data.evaluation.isFirstEvaluation,
          id: data.evaluation.id,
          date: data.evaluation.date,
        };
      }

      if (data.reevaluations && data.reevaluations.length > 0) {
        this.model.reevaluations = [];
        data.reevaluations.forEach((item) => {
          this.model.reevaluations.push({
            id: item.id,
            diagnosis: item.diagnosis,
            date: this.formatDate(item.date),
            confirmedDate: item.confirmedDate,
          });
        });
      }

      if (data.strengths) {
        data.strengths.forEach((item) => {
          this.catalog.addedStrengths.push({
            id: item.id,
            name: item.name,
            highlight: true,
          });
        });

        this.model.strengths = this.catalog.addedStrengths;
      }

      if (data.specialistSignature) {
        this.model.specialistSignature.push({
          id: data.specialistSignature.id,
          name: data.specialistSignature.name,
          signed: true,
          email: data.specialistSignature.email,
          phone: data.specialistSignature.phone,
          role: {
            id: data.specialistSignature.role.id,
            name: data.specialistSignature.role.name,
          },
        });
      }

      this.catalog.recomendedTermChecks =
        data.questions?.sections?.[0]?.content?.[1]?.questions?.[1] || null;

      const answers = data.questions?.sections?.[0]?.answers || [];

      this.catalog.recomendedTermChecks?.answers.forEach((item) => {
        item.isSelected =
          answers.find((x) => x.questionId == 751 && x.answerId == item.id) !=
          null;
      });

      this.catalog.recoveryStudentCapacityChecks =
        data.questions?.sections?.[0]?.content?.[1]?.questions?.[2] || null;

      this.catalog.recoveryStudentCapacityChecks?.answers.forEach((item) => {
        item.isSelected =
          answers.find((x) => x.questionId == 752 && x.answerId == item.id) !=
          null;
      });

      this.requiresContinueServiceOptions.forEach((item) => {
        item.check =
          answers.find((x) => x.questionId == 750 && x.answerId == item.id) !=
          null;
      });

      this.requiresContinueService = this.requiresContinueServiceOptions.find(
        (x) => x.check
      );

      this.recomendedTermOptions.forEach((item) => {
        item.check =
          answers.find((x) => x.questionId == 751 && x.answerId == item.id) !=
          null;
      });

      this.recomendedTerm = this.recomendedTermOptions.find((x) => x.check);

      this.recoveryStudentCapacityOptions.forEach((item) => {
        item.check =
          answers.find((x) => x.questionId == 752 && x.answerId == item.id) !=
          null;
      });

      this.recoveryStudentCapacity = this.recoveryStudentCapacityOptions.find(
        (x) => x.check
      );

      this.studentFactorsOptions.forEach((item) => {
        item.check =
          answers.find((x) => x.questionId == item.id && x.answerId == 138) !=
          null;
      });

      this.studentFactors = this.studentFactorsOptions.find((x) => x.check);

      this.studentFactorsOthersOptions.forEach((item) => {
        item.check =
          answers.find((x) => x.questionId == item.id && x.answerId == 138) !=
          null;
      });

      this.studentFactorsOther = this.studentFactorsOthersOptions.find(
        (x) => x.check
      );

      if (data.resumeType) {
        this.interventionResumeOptions.forEach((item) => {
          item.check = data.resumeType == item.id;
        });

        this.model.resumeType = this.interventionResumeOptions.find(
          (x) => x.check
        );
      }
      this.model.resume = data.resume;

      if (data.analysis) {
        this.model.analysis = data.analysis;
        this.yearsOrMonthsValue = data.analysis.months || data.analysis.years;

        const selected = data.analysis.months
          ? 2
          : data.analysis.years
          ? 1
          : null;

        this.recommendedOptions.forEach((item) => {
          item.check = selected == item.id;
        });

        this.yearsOrMonths = this.recommendedOptions.find((x) => x.check);

        this.model.analysis.frequencyTypeId = this.catalog.frequencyTypes.find(
          (x) => x.id == data.analysis.frequencyTypeId
        );

        this.model.analysis.frequencyTypeCheck = !!this.model.analysis
          .frequencyTypeId;

        this.model.analysis.serviceModalityId = this.catalog.serviceModalities.find(
          (x) => x.id == data.analysis.serviceModalityId
        );

        this.model.analysis.serviceModalityCheck = !!this.model.analysis
          .serviceModalityId;

        this.model.analysis.serviceDurationId = this.catalog.serviceDurations.find(
          (x) => x.id == data.analysis.serviceDurationId
        );

        this.model.analysis.serviceDurationCheck = !!this.model.analysis
          .serviceDurationId;

        this.model.analysis.periodTypeId = this.catalog.periods.find(
          (x) => x.id == data.analysis.periodTypeId
        );

        this.model.analysis.periodCheck = !!this.model.analysis.periodTypeId;
      } else {
        this.yearsOrMonths = null;
      }

      if (data.goals) {
        data.goals.forEach((item) => {
          this.model.goals.push({
            id: item.id,
            academicYear: this.catalog.academicYears.find(
              (x) => x.id == item.academicYearId
            ),
            executionLevelArchieved: this.catalog.executionLevelArchieved.find(
              (x) => x.id == item.executionLevelArchievedId
            ),
            executionLevelPercent: item.percent,
            selectedGoal: { id: 1, name: item.goal },
          });
        });
      }
      // JSF: Mark the checkboxes for Section Factors.
      this.setFactorsCheckBox(answers, data.otherFactor);

      this.model.id = data.id;
      this.model.isSubmitted = data.isSubmitted;
      this.continuityServiceComment = data.continuityServiceComment;
      this.isPriorAcademicYear = data.isPriorAcademicYear;
    },

    mapFactors(factors) {
      return factors.map((factor) => ({
        id: factor.id,
        name: factor.id.toString(),
        value: factor.id,
        label: factor.name,
        check: false,
        invalid: false,
      }));
    },
    async onStrengthContinue() {
      if (this.catalog.addedStrengths.length == 0) {
        this.ShowToast(
          'Error',
          'Al menos una fortaleza es requerida.',
          'error'
        );

        return;
      }
      this.model.strengths = this.catalog.addedStrengths;
      this.isAddStrengthMode = false;
    },
    async printResultSummary() {
      let report = `referral/resultsummaryreport/${this.admissionId}/${this.model.id}`;
      await _generalApi.downloadReport(report);
    },
    onChangeStudentFactor() {
      if (this.studentFactors.id != this.factorQuestionIdNamedOther) {
        this.studentFactorsOther = { id: null };
      }
    },
    async getSpecialists() {
      const isAdmission = false;
      const specialistId = 0;
      //Services Specilists
      const { data: serviceSpecialists } = await getSpecialistsForAssignment(
        this.admissionId,
        this.admissionInfo.locationFiscalYearId,
        this.admissionInfo.studentSieId,
        specialistId,
        isAdmission
      );

      this.serviceSpecialists = serviceSpecialists;

      //Plan's Writer
      if (this.admissionInfo.companyId || this.user.profile.companyid) {
        const { data: writerSpecialists } = await getSpecialistList({
          fiscalYearId: this.admissionInfo.academicYearId,
          providerId: this.admissionInfo.companyId,
          disciplineTypeId: this.admissionInfo.disciplineTypeId,
          validateUserProfile: false, //para no evaluar el perfil de usuario.
          validateLicense: true,
        });

        this.writerSpecialists = writerSpecialists;
      }
    },
    async getSpecialistDetail() {
      const isSpecialistServiceSelected =
        this.specialistModel &&
        this.specialistModel.serviceSpecialist &&
        this.specialistModel.serviceSpecialist.id > 0;
      if (!isSpecialistServiceSelected) {
        this.specialistModel.selectedServiceSpecialist = {
          fullName: '',
          licenceNo: '',
          expirationDate: '',
        };
        return;
      }

      const { data: specialistDetail } = await _getSpecialistInfo(
        this.specialistModel.serviceSpecialist.id,
        this.admissionInfo.disciplineTypeId
      );

      this.specialistModel.selectedServiceSpecialist = specialistDetail;
      this.specialistModel.selectedServiceSpecialist.expirationDate = specialistDetail?.expirationDate
        ? this.formatDate(specialistDetail?.expirationDate)
        : '';
    },
    formatDate(date) {
      if (date) {
        return moment(date)
          .locale('es-PR')
          .format('DD/MMM/YYYY');
      }
    },
    async fetchParameters() {
      const { data } = await getSysparameterByNames({
        parameterNames: [
          sysparameterByNames.ResultSummaryFeatureImprovementEnabled,
        ],
      });

      this.parameterValues = [...data];
    },
    isGoalValid(goal) {
      const isValid =
        goal.academicYear &&
        goal.executionLevelArchieved &&
        (goal.executionLevelArchieved.id === EXECUTION_LEVEL_NA || //No Aplica
          (goal.executionLevelPercent !== null &&
            goal.executionLevelPercent !== undefined)) &&
        goal.selectedGoal &&
        goal.selectedGoal.name.trim() !== '';

      return isValid;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .custom-signature__extended {
  margin-top: -40px !important;
  .tag-badge-title-info {
    display: none;
  }
}
</style>
